export type ScriptParamWidget =
  | "slider"
  | "number"
  | "text"
  | "textarea"
  | "toggle"
  | "dropdown"
  | "color"
  | "gradient";

export type ScriptParamValue = number | string | boolean | ScriptGradientParamValue;

/** Luau-facing gradient table (CSS colors in stops), injectable as a fill. */
export interface ScriptGradientParamValue {
  type: "linear" | "radial";
  angle?: number;
  radius?: number;
  colorSpace?: "oklch" | "rgb";
  stops: Array<{ position: number; color: string }>;
  start?: { x: number; y: number };
  end?: { x: number; y: number };
  center?: { x: number; y: number };
}

export function isScriptGradientParamValue(value: ScriptParamValue): value is ScriptGradientParamValue {
  return typeof value === "object"
    && value !== null
    && Array.isArray((value as ScriptGradientParamValue).stops)
    && ((value as ScriptGradientParamValue).type === "linear"
      || (value as ScriptGradientParamValue).type === "radial");
}

export interface ScriptParamOption {
  label: string;
  value: ScriptParamValue;
}

export interface ScriptParam {
  name: string;
  label: string;
  widget: ScriptParamWidget;
  min?: number;
  max?: number;
  step?: number;
  options?: ScriptParamOption[];
  defaultValue: ScriptParamValue;
  /** 0-based line index in the source. */
  lineIndex: number;
}

interface ParsedRhs {
  kind: ValueKind | "table";
  value: ScriptParamValue;
  isInt?: boolean;
  raw: string;
}

type ValueKind = "number" | "string" | "boolean";

interface ParamArgs {
  type?: string;
  min?: number;
  max?: number;
  step?: number;
  default?: ScriptParamValue;
  label?: string;
  options?: ScriptParamOption[];
}

const ASSIGNMENT_RE = /^(local\s+)?([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.+?)\s*;?\s*$/;

/**
 * Parse a top-level `-- @desc` … `-- @descend` (or `-- @enddesc`) block.
 * Also accepts a one-liner: `-- @desc Short summary`.
 */
export function parseScriptDescription(source: string): string | undefined {
  const lines = splitLines(source);
  let blockStart: number | undefined;

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex]!;
    const trimmed = line.trim();

    if (blockStart === undefined) {
      if (trimmed === "") {
        continue;
      }
      const annotation = parseLeadingCommentAnnotation(trimmed);
      if (!annotation) {
        // Non-comment / code before any @desc — no description.
        return undefined;
      }
      if (annotation.name === "desc") {
        if (annotation.rest) {
          return annotation.rest;
        }
        blockStart = lineIndex + 1;
        continue;
      }
      // Other leading comment annotations / comment lines — keep scanning.
      continue;
    }

    const annotation = parseLeadingCommentAnnotation(trimmed);
    if (annotation && (annotation.name === "descend" || annotation.name === "enddesc")) {
      return normalizeDescriptionLines(lines.slice(blockStart, lineIndex));
    }
    if (!isFullLineComment(trimmed) && trimmed !== "") {
      // Unterminated block.
      return undefined;
    }
  }

  return undefined;
}

function parseLeadingCommentAnnotation(
  trimmedLine: string,
): { name: string; rest: string } | undefined {
  if (!trimmedLine.startsWith("--")) {
    return undefined;
  }
  const body = trimmedLine.slice(2).trimStart();
  if (!body.startsWith("@")) {
    return undefined;
  }
  let index = 1;
  while (index < body.length && /[A-Za-z_]/.test(body[index]!)) {
    index += 1;
  }
  const name = body.slice(1, index).toLowerCase();
  if (!name) {
    return undefined;
  }
  const rest = body.slice(index).trim();
  return { name, rest };
}

function isFullLineComment(trimmedLine: string): boolean {
  return trimmedLine.startsWith("--");
}

function normalizeDescriptionLines(lines: string[]): string | undefined {
  const body = lines.map(line => {
    const trimmed = line.trim();
    if (!trimmed.startsWith("--")) {
      return "";
    }
    const afterDashes = trimmed.slice(2);
    return afterDashes.startsWith(" ") ? afterDashes.slice(1) : afterDashes;
  });
  while (body.length > 0 && body[0]!.trim() === "") {
    body.shift();
  }
  while (body.length > 0 && body[body.length - 1]!.trim() === "") {
    body.pop();
  }
  const text = body.join("\n").trim();
  return text.length > 0 ? text : undefined;
}

/**
 * Parse top-level Luau assignments annotated with trailing `-- @param(...)`.
 */
export function parseScriptParams(source: string): ScriptParam[] {
  const lines = splitLines(source);
  const params: ScriptParam[] = [];

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex]!;
    const commentIndex = findLineCommentStart(line);
    if (commentIndex < 0) {
      continue;
    }

    const code = line.slice(0, commentIndex).trim();
    const comment = line.slice(commentIndex + 2).trim();
    const assignment = code.match(ASSIGNMENT_RE);
    if (!assignment) {
      continue;
    }

    const name = assignment[2]!;
    const rhsRaw = assignment[3]!;
    const rhs = parseRhs(rhsRaw);
    if (!rhs) {
      continue;
    }

    const annotations = lexAnnotations(comment);
    const paramAnnotation = annotations.find(annotation => annotation.name === "param");
    if (!paramAnnotation) {
      continue;
    }

    const args = parseParamArgs(paramAnnotation.argsText ?? "");
    params.push(buildParam({ name, rhs, args, lineIndex }));
  }

  return params;
}

/**
 * Rewrite annotated assignment RHS values for a single run.
 * Returns a new source string; does not mutate the document script.
 */
export function applyScriptParamValues(
  source: string,
  values: Record<string, ScriptParamValue>,
): string {
  const params = parseScriptParams(source);
  if (params.length === 0) {
    return source;
  }

  const byLine = new Map(params.map(param => [param.lineIndex, param]));
  const lines = splitLines(source);
  const next = lines.map((line, lineIndex) => {
    const param = byLine.get(lineIndex);
    if (!param || !(param.name in values)) {
      return line;
    }
    return rewriteAssignmentRhs(line, serializeLuauValue(values[param.name]!, param.widget));
  });
  return next.join("\n");
}

export function defaultValuesForParams(params: ScriptParam[]): Record<string, ScriptParamValue> {
  const values: Record<string, ScriptParamValue> = {};
  for (const param of params) {
    values[param.name] = param.defaultValue;
  }
  return values;
}

/**
 * Param live-preview only works for one-shot `init` scripts.
 * Scripts that define global `update` / `draw` keep a VM session and cannot
 * safely ghost-render into a disposable working copy.
 * Scripts may also opt out with a top-level `-- @nopreview` directive.
 */
export function scriptSourceSupportsParamLivePreview(source: string): boolean {
  if (scriptSourceHasNoPreviewDirective(source)) {
    return false;
  }
  return !/(^|\n)\s*function\s+(update|draw)\s*\(/.test(source);
}

/** True when the source has a full-line `-- @nopreview` comment directive. */
export function scriptSourceHasNoPreviewDirective(source: string): boolean {
  for (const line of splitLines(source)) {
    const annotation = parseLeadingCommentAnnotation(line.trim());
    if (annotation?.name === "nopreview") {
      return true;
    }
  }
  return false;
}

export function serializeLuauValue(value: ScriptParamValue, widget?: ScriptParamWidget): string {
  if (widget === "toggle" || typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return "0";
    }
    return String(value);
  }
  if (isScriptGradientParamValue(value) || widget === "gradient") {
    const gradient = isScriptGradientParamValue(value)
      ? value
      : defaultGradientParamValue();
    return serializeLuauGradient(gradient);
  }
  return quoteLuauString(String(value));
}

export function defaultGradientParamValue(): ScriptGradientParamValue {
  return {
    type: "linear",
    angle: 90,
    colorSpace: "oklch",
    stops: [
      { position: 0, color: "#0ea5e9" },
      { position: 1, color: "#6366f1" },
    ],
  };
}

export function defaultColorParamValue(): string {
  return "#0ea5e9";
}

function serializeLuauGradient(gradient: ScriptGradientParamValue): string {
  const parts: string[] = [`type = "${gradient.type === "radial" ? "radial" : "linear"}"`];
  if (gradient.type !== "radial" && gradient.angle !== undefined) {
    parts.push(`angle = ${serializeNumber(gradient.angle)}`);
  }
  if (gradient.type === "radial" && gradient.radius !== undefined) {
    parts.push(`radius = ${serializeNumber(gradient.radius)}`);
  }
  if (gradient.colorSpace) {
    parts.push(`colorSpace = "${gradient.colorSpace}"`);
  }
  if (gradient.start) {
    parts.push(`start = ${serializeLuauPoint(gradient.start)}`);
  }
  if (gradient.end) {
    parts.push(`["end"] = ${serializeLuauPoint(gradient.end)}`);
  }
  if (gradient.center) {
    parts.push(`center = ${serializeLuauPoint(gradient.center)}`);
  }
  const stops = gradient.stops
    .map(stop => `{ position = ${serializeNumber(stop.position)}, color = ${quoteLuauString(stop.color)} }`)
    .join(", ");
  parts.push(`stops = { ${stops} }`);
  return `{ ${parts.join(", ")} }`;
}

function serializeLuauPoint(point: { x: number; y: number }): string {
  return `{ x = ${serializeNumber(point.x)}, y = ${serializeNumber(point.y)} }`;
}

function serializeNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return "0";
  }
  return String(value);
}

function rewriteAssignmentRhs(line: string, serialized: string): string {
  const commentIndex = findLineCommentStart(line);
  if (commentIndex < 0) {
    return line;
  }
  const code = line.slice(0, commentIndex);
  const comment = line.slice(commentIndex);
  const match = code.match(/^(.*?\b[A-Za-z_][A-Za-z0-9_]*\s*=\s*)(.+?)(\s*;?\s*)$/);
  if (!match) {
    return line;
  }
  return `${match[1]}${serialized}${match[3]}${comment}`;
}

function buildParam(input: {
  name: string;
  rhs: ParsedRhs;
  args: ParamArgs;
  lineIndex: number;
}): ScriptParam {
  const widget = resolveWidget(input.rhs, input.args);
  const label = input.args.label?.trim() || humanizeName(input.name);
  const defaultValue = resolveDefault(input.rhs, input.args, widget);
  const bounds = resolveNumericBounds(input.rhs, input.args, widget);

  return {
    name: input.name,
    label,
    widget,
    ...bounds,
    options: input.args.options,
    defaultValue,
    lineIndex: input.lineIndex,
  };
}

function resolveWidget(rhs: ParsedRhs, args: ParamArgs): ScriptParamWidget {
  const requested = args.type?.toLowerCase();
  if (requested === "slider" || requested === "number") {
    return requested;
  }
  if (requested === "text" || requested === "textarea") {
    return requested;
  }
  if (requested === "toggle") {
    return "toggle";
  }
  if (requested === "dropdown") {
    return "dropdown";
  }
  if (requested === "color") {
    return "color";
  }
  if (requested === "gradient") {
    return "gradient";
  }

  if (rhs.kind === "boolean") {
    return "toggle";
  }
  if (rhs.kind === "table") {
    return "gradient";
  }
  if (rhs.kind === "string") {
    return "text";
  }
  return "slider";
}

function resolveDefault(rhs: ParsedRhs, args: ParamArgs, widget: ScriptParamWidget): ScriptParamValue {
  if (args.default !== undefined) {
    return coerceValue(args.default, widget);
  }
  return coerceValue(rhs.value, widget);
}

function resolveNumericBounds(
  rhs: ParsedRhs,
  args: ParamArgs,
  widget: ScriptParamWidget,
): { min?: number; max?: number; step?: number } {
  if (widget !== "slider" && widget !== "number") {
    return {};
  }
  const isInt = rhs.isInt === true
    || (typeof args.default === "number" && Number.isInteger(args.default));
  return {
    min: args.min ?? 0,
    max: args.max ?? (isInt ? 10 : 1),
    step: args.step ?? (isInt ? 1 : 0.01),
  };
}

function coerceValue(value: ScriptParamValue, widget: ScriptParamWidget): ScriptParamValue {
  if (widget === "toggle") {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value !== 0;
    }
    return value === "true" || value === "1";
  }
  if (widget === "slider" || widget === "number") {
    const number = typeof value === "number" ? value : Number(value);
    return Number.isFinite(number) ? number : 0;
  }
  if (widget === "color") {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    return defaultColorParamValue();
  }
  if (widget === "gradient") {
    if (isScriptGradientParamValue(value)) {
      return normalizeGradientParamValue(value);
    }
    if (typeof value === "string" && value.trim()) {
      return {
        ...defaultGradientParamValue(),
        stops: [
          { position: 0, color: value.trim() },
          { position: 1, color: "#ffffff" },
        ],
      };
    }
    return defaultGradientParamValue();
  }
  if (isScriptGradientParamValue(value)) {
    return serializeLuauGradient(value);
  }
  return String(value);
}

function normalizeGradientParamValue(value: ScriptGradientParamValue): ScriptGradientParamValue {
  const type = value.type === "radial" ? "radial" : "linear";
  const stops = (value.stops ?? [])
    .map(stop => ({
      position: clamp01(Number(stop.position) || 0),
      color: String(stop.color || "#000000"),
    }))
    .sort((left, right) => left.position - right.position);
  while (stops.length < 2) {
    stops.push({
      position: stops.length === 0 ? 0 : 1,
      color: stops.length === 0 ? "#0ea5e9" : "#ffffff",
    });
  }
  return {
    type,
    angle: type === "linear" ? (Number.isFinite(value.angle) ? value.angle : 90) : undefined,
    radius: type === "radial" ? (Number.isFinite(value.radius) ? value.radius : 0.5) : undefined,
    colorSpace: value.colorSpace === "rgb" ? "rgb" : "oklch",
    stops,
    start: value.start,
    end: value.end,
    center: value.center,
  };
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function parseRhs(raw: string): ParsedRhs | undefined {
  const trimmed = raw.trim();
  if (trimmed === "true" || trimmed === "false") {
    return { kind: "boolean", value: trimmed === "true", raw: trimmed };
  }

  if (trimmed !== "" && /^-?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(trimmed)) {
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber)) {
      return {
        kind: "number",
        value: asNumber,
        isInt: Number.isInteger(asNumber) && !/[.eE]/.test(trimmed),
        raw: trimmed,
      };
    }
  }

  const stringLiteral = parseStringLiteral(trimmed);
  if (stringLiteral !== undefined) {
    return { kind: "string", value: stringLiteral, raw: trimmed };
  }

  if (trimmed.startsWith("{")) {
    const table = parseLuauTableLiteral(trimmed);
    if (table && isScriptGradientParamValue(table as ScriptParamValue)) {
      return {
        kind: "table",
        value: normalizeGradientParamValue(table as ScriptGradientParamValue),
        raw: trimmed,
      };
    }
    const asGradient = tableValueToGradient(table);
    if (asGradient) {
      return { kind: "table", value: asGradient, raw: trimmed };
    }
  }

  return undefined;
}

function tableValueToGradient(value: unknown): ScriptGradientParamValue | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const typeRaw = String(record.type ?? "linear").toLowerCase();
  const type = typeRaw === "radial" || typeRaw === "radialgradient" ? "radial" : "linear";
  const stopsRaw = record.stops;
  if (!Array.isArray(stopsRaw)) {
    return undefined;
  }
  const stops = stopsRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") {
        return undefined;
      }
      const stop = entry as Record<string, unknown>;
      const position = Number(stop.position);
      const color = typeof stop.color === "string" ? stop.color : undefined;
      if (!Number.isFinite(position) || !color) {
        return undefined;
      }
      return { position, color };
    })
    .filter((stop): stop is { position: number; color: string } => stop !== undefined);
  if (stops.length < 2) {
    return undefined;
  }
  return normalizeGradientParamValue({
    type,
    angle: typeof record.angle === "number" ? record.angle : undefined,
    radius: typeof record.radius === "number" ? record.radius : undefined,
    colorSpace: record.colorSpace === "rgb" ? "rgb" : "oklch",
    stops,
    start: asPoint(record.start),
    end: asPoint(record.end),
    center: asPoint(record.center),
  });
}

function asPoint(value: unknown): { x: number; y: number } | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const x = Number(record.x);
  const y = Number(record.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return undefined;
  }
  return { x, y };
}

/** Minimal Luau table literal parser for gradient defaults / injection round-trips. */
function parseLuauTableLiteral(source: string): unknown | undefined {
  const parser = { source, index: 0 };
  try {
    const value = parseLuauValue(parser);
    skipWs(parser);
    if (parser.index !== parser.source.length) {
      return undefined;
    }
    return value;
  } catch {
    return undefined;
  }
}

function parseLuauValue(parser: { source: string; index: number }): unknown {
  skipWs(parser);
  const char = parser.source[parser.index];
  if (char === "{") {
    return parseLuauTable(parser);
  }
  if (char === '"' || char === "'") {
    return parseLuauString(parser);
  }
  if (char === "-" || (char !== undefined && /\d/.test(char))) {
    return parseLuauNumber(parser);
  }
  if (matchKeyword(parser, "true")) {
    return true;
  }
  if (matchKeyword(parser, "false")) {
    return false;
  }
  if (matchKeyword(parser, "nil")) {
    return null;
  }
  throw new Error("unexpected value");
}

function parseLuauTable(parser: { source: string; index: number }): unknown {
  consume(parser, "{");
  skipWs(parser);
  const object: Record<string, unknown> = {};
  const array: unknown[] = [];
  let arrayMode = true;
  let arrayIndex = 0;

  while (parser.index < parser.source.length && parser.source[parser.index] !== "}") {
    skipWs(parser);
    if (parser.source[parser.index] === "}") {
      break;
    }

    const start = parser.index;
    const key = tryParseLuauKey(parser);
    skipWs(parser);
    if (key !== undefined && parser.source[parser.index] === "=") {
      arrayMode = false;
      parser.index += 1;
      const value = parseLuauValue(parser);
      object[key] = value;
    } else {
      parser.index = start;
      const value = parseLuauValue(parser);
      if (arrayMode) {
        array.push(value);
        arrayIndex += 1;
      } else {
        object[String(arrayIndex + 1)] = value;
        arrayIndex += 1;
      }
    }

    skipWs(parser);
    if (parser.source[parser.index] === ",") {
      parser.index += 1;
      continue;
    }
    if (parser.source[parser.index] === ";") {
      parser.index += 1;
    }
  }
  consume(parser, "}");
  if (arrayMode) {
    return array;
  }
  return object;
}

function tryParseLuauKey(parser: { source: string; index: number }): string | undefined {
  skipWs(parser);
  if (parser.source[parser.index] === "[") {
    parser.index += 1;
    skipWs(parser);
    const key = parseLuauValue(parser);
    skipWs(parser);
    consume(parser, "]");
    return String(key);
  }
  const start = parser.index;
  if (parser.source[start] && /[A-Za-z_]/.test(parser.source[start]!)) {
    parser.index += 1;
    while (parser.index < parser.source.length && /[A-Za-z0-9_]/.test(parser.source[parser.index]!)) {
      parser.index += 1;
    }
    return parser.source.slice(start, parser.index);
  }
  return undefined;
}

function parseLuauString(parser: { source: string; index: number }): string {
  const quote = parser.source[parser.index]!;
  parser.index += 1;
  let out = "";
  while (parser.index < parser.source.length) {
    const char = parser.source[parser.index]!;
    if (char === "\\") {
      const escaped = decodeLuauEscape(parser.source, parser.index + 1);
      if (!escaped) {
        throw new Error("bad escape");
      }
      out += escaped.char;
      parser.index = escaped.nextIndex;
      continue;
    }
    if (char === quote) {
      parser.index += 1;
      return out;
    }
    out += char;
    parser.index += 1;
  }
  throw new Error("unterminated string");
}

function parseLuauNumber(parser: { source: string; index: number }): number {
  const start = parser.index;
  if (parser.source[parser.index] === "-") {
    parser.index += 1;
  }
  while (parser.index < parser.source.length && /[0-9.eE+-]/.test(parser.source[parser.index]!)) {
    parser.index += 1;
  }
  const raw = parser.source.slice(start, parser.index);
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    throw new Error("bad number");
  }
  return value;
}

function matchKeyword(parser: { source: string; index: number }, keyword: string): boolean {
  if (!parser.source.startsWith(keyword, parser.index)) {
    return false;
  }
  const next = parser.source[parser.index + keyword.length];
  if (next && /[A-Za-z0-9_]/.test(next)) {
    return false;
  }
  parser.index += keyword.length;
  return true;
}

function consume(parser: { source: string; index: number }, expected: string): void {
  if (parser.source[parser.index] !== expected) {
    throw new Error(`expected ${expected}`);
  }
  parser.index += 1;
}

function skipWs(parser: { source: string; index: number }): void {
  while (parser.index < parser.source.length && /\s/.test(parser.source[parser.index]!)) {
    parser.index += 1;
  }
}

function parseStringLiteral(raw: string): string | undefined {
  if (raw.length < 2) {
    return undefined;
  }
  const quote = raw[0];
  if ((quote !== '"' && quote !== "'") || raw[raw.length - 1] !== quote) {
    return undefined;
  }
  let out = "";
  for (let index = 1; index < raw.length - 1; index += 1) {
    const char = raw[index]!;
    if (char === "\\") {
      const escaped = decodeLuauEscape(raw, index + 1);
      if (!escaped) {
        return undefined;
      }
      out += escaped.char;
      index = escaped.nextIndex - 1;
      continue;
    }
    out += char;
  }
  return out;
}

/** Decode one Luau/Lua string escape starting at `escapeIndex` (char after `\`). */
function decodeLuauEscape(
  source: string,
  escapeIndex: number,
): { char: string; nextIndex: number } | undefined {
  const code = source[escapeIndex];
  if (code === undefined) {
    return undefined;
  }
  switch (code) {
    case "a":
      return { char: "\u0007", nextIndex: escapeIndex + 1 };
    case "b":
      return { char: "\b", nextIndex: escapeIndex + 1 };
    case "f":
      return { char: "\f", nextIndex: escapeIndex + 1 };
    case "n":
      return { char: "\n", nextIndex: escapeIndex + 1 };
    case "r":
      return { char: "\r", nextIndex: escapeIndex + 1 };
    case "t":
      return { char: "\t", nextIndex: escapeIndex + 1 };
    case "v":
      return { char: "\v", nextIndex: escapeIndex + 1 };
    case "\\":
    case "\"":
    case "'":
    case "\n":
      return { char: code, nextIndex: escapeIndex + 1 };
    case "x": {
      const hex = source.slice(escapeIndex + 1, escapeIndex + 3);
      if (!/^[0-9a-fA-F]{2}$/.test(hex)) {
        return undefined;
      }
      return {
        char: String.fromCharCode(Number.parseInt(hex, 16)),
        nextIndex: escapeIndex + 3,
      };
    }
    case "u": {
      if (source[escapeIndex + 1] !== "{") {
        return undefined;
      }
      let end = escapeIndex + 2;
      while (end < source.length && /[0-9a-fA-F]/.test(source[end]!)) {
        end += 1;
      }
      if (source[end] !== "}" || end === escapeIndex + 2) {
        return undefined;
      }
      const codePoint = Number.parseInt(source.slice(escapeIndex + 2, end), 16);
      if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
        return undefined;
      }
      return {
        char: String.fromCodePoint(codePoint),
        nextIndex: end + 1,
      };
    }
    default: {
      if (/[0-9]/.test(code)) {
        let end = escapeIndex;
        while (end < source.length && end - escapeIndex < 3 && /[0-9]/.test(source[end]!)) {
          end += 1;
        }
        const value = Number.parseInt(source.slice(escapeIndex, end), 10);
        if (!Number.isFinite(value) || value > 255) {
          return undefined;
        }
        return {
          char: String.fromCharCode(value),
          nextIndex: end,
        };
      }
      // Unknown escape: keep the escaped character (Lua-ish leniency).
      return { char: code, nextIndex: escapeIndex + 1 };
    }
  }
}

function quoteLuauString(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n")}"`;
}

function splitLines(source: string): string[] {
  return source.split(/\n/);
}

function findLineCommentStart(line: string): number {
  let inSingle = false;
  let inDouble = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]!;
    const prev = line[index - 1];
    if (inSingle) {
      if (char === "'" && prev !== "\\") {
        inSingle = false;
      }
      continue;
    }
    if (inDouble) {
      if (char === '"' && prev !== "\\") {
        inDouble = false;
      }
      continue;
    }
    if (char === "'") {
      inSingle = true;
      continue;
    }
    if (char === '"') {
      inDouble = true;
      continue;
    }
    if (char === "-" && line[index + 1] === "-") {
      return index;
    }
  }
  return -1;
}

interface AnnotationToken {
  name: string;
  argsText?: string;
}

function lexAnnotations(region: string): AnnotationToken[] {
  const tokens: AnnotationToken[] = [];
  let index = 0;
  while (index < region.length) {
    while (index < region.length && /\s/.test(region[index]!)) {
      index += 1;
    }
    if (region[index] !== "@") {
      break;
    }
    index += 1;
    const nameStart = index;
    while (index < region.length && /[A-Za-z_]/.test(region[index]!)) {
      index += 1;
    }
    const name = region.slice(nameStart, index).toLowerCase();
    if (!name) {
      break;
    }
    while (index < region.length && /\s/.test(region[index]!)) {
      index += 1;
    }
    if (region[index] === "(") {
      const closed = readBalanced(region, index);
      if (!closed) {
        break;
      }
      tokens.push({ name, argsText: region.slice(index + 1, closed) });
      index = closed + 1;
    } else {
      tokens.push({ name });
    }
  }
  return tokens;
}

function readBalanced(text: string, openIndex: number): number | undefined {
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  for (let index = openIndex; index < text.length; index += 1) {
    const char = text[index]!;
    const prev = text[index - 1];
    if (inSingle) {
      if (char === "'" && prev !== "\\") {
        inSingle = false;
      }
      continue;
    }
    if (inDouble) {
      if (char === '"' && prev !== "\\") {
        inDouble = false;
      }
      continue;
    }
    if (char === "'") {
      inSingle = true;
      continue;
    }
    if (char === '"') {
      inDouble = true;
      continue;
    }
    if (char === "(") {
      depth += 1;
      continue;
    }
    if (char === ")") {
      depth -= 1;
      if (depth === 0) {
        return index;
      }
    }
  }
  return undefined;
}

function parseParamArgs(argsText: string): ParamArgs {
  const args: ParamArgs = {};
  if (!argsText.trim()) {
    return args;
  }

  for (const rawArg of splitTopLevelArgs(argsText)) {
    const trimmed = rawArg.trim();
    if (!trimmed) {
      continue;
    }
    const eq = findTopLevelEquals(trimmed);
    if (eq < 0) {
      continue;
    }
    const key = trimmed.slice(0, eq).trim().toLowerCase();
    const valueText = trimmed.slice(eq + 1).trim();

    if (key === "type") {
      const typeMatch = valueText.match(/^([A-Za-z_][A-Za-z0-9_-]*)\s*(?:\((.*)\))?$/s);
      if (!typeMatch) {
        args.type = valueText.toLowerCase();
        continue;
      }
      args.type = typeMatch[1]!.toLowerCase();
      const optionBody = typeMatch[2];
      if (optionBody !== undefined && args.type === "dropdown") {
        args.options = parseDropdownOptions(optionBody);
      }
      continue;
    }

    if (key === "label") {
      args.label = parseStringLiteral(valueText) ?? unquoteLoose(valueText);
      continue;
    }

    if (key === "min" || key === "max" || key === "step") {
      const number = Number(valueText);
      if (Number.isFinite(number)) {
        args[key] = number;
      }
      continue;
    }

    if (key === "default") {
      const rhs = parseRhs(valueText);
      if (rhs) {
        args.default = rhs.value;
      } else {
        const asString = parseStringLiteral(valueText);
        if (asString !== undefined) {
          args.default = asString;
        }
      }
    }
  }

  return args;
}

function parseDropdownOptions(body: string): ScriptParamOption[] {
  const options: ScriptParamOption[] = [];
  for (const raw of splitTopLevelArgs(body)) {
    const trimmed = raw.trim();
    if (!trimmed) {
      continue;
    }
    const eq = findTopLevelEquals(trimmed);
    if (eq < 0) {
      const label = parseStringLiteral(trimmed) ?? unquoteLoose(trimmed);
      options.push({ label, value: label });
      continue;
    }
    const label = parseStringLiteral(trimmed.slice(0, eq).trim()) ?? unquoteLoose(trimmed.slice(0, eq).trim());
    const valueText = trimmed.slice(eq + 1).trim();
    const rhs = parseRhs(valueText);
    if (rhs) {
      options.push({ label, value: rhs.value });
    } else {
      options.push({ label, value: parseStringLiteral(valueText) ?? unquoteLoose(valueText) });
    }
  }
  return options;
}

function splitTopLevelArgs(text: string): string[] {
  const parts: string[] = [];
  let start = 0;
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    const prev = text[index - 1];
    if (inSingle) {
      if (char === "'" && prev !== "\\") {
        inSingle = false;
      }
      continue;
    }
    if (inDouble) {
      if (char === '"' && prev !== "\\") {
        inDouble = false;
      }
      continue;
    }
    if (char === "'") {
      inSingle = true;
      continue;
    }
    if (char === '"') {
      inDouble = true;
      continue;
    }
    if (char === "(") {
      depth += 1;
      continue;
    }
    if (char === ")") {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth !== 0) {
      continue;
    }
    // Comma-separated (shady) or space-separated (plan examples).
    if (char === ",") {
      parts.push(text.slice(start, index));
      start = index + 1;
      continue;
    }
    if (/\s/.test(char) && looksLikeArgStart(text, index + 1)) {
      parts.push(text.slice(start, index));
      start = index + 1;
      while (start < text.length && /\s/.test(text[start]!)) {
        start += 1;
      }
      index = start - 1;
    }
  }
  parts.push(text.slice(start));
  return parts;
}

/** True when `index` starts a `key=` arg at top level. */
function looksLikeArgStart(text: string, index: number): boolean {
  let cursor = index;
  while (cursor < text.length && /\s/.test(text[cursor]!)) {
    cursor += 1;
  }
  if (cursor >= text.length || !/[A-Za-z_]/.test(text[cursor]!)) {
    return false;
  }
  while (cursor < text.length && /[A-Za-z0-9_]/.test(text[cursor]!)) {
    cursor += 1;
  }
  while (cursor < text.length && /\s/.test(text[cursor]!)) {
    cursor += 1;
  }
  return text[cursor] === "=";
}

function findTopLevelEquals(text: string): number {
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    const prev = text[index - 1];
    if (inSingle) {
      if (char === "'" && prev !== "\\") {
        inSingle = false;
      }
      continue;
    }
    if (inDouble) {
      if (char === '"' && prev !== "\\") {
        inDouble = false;
      }
      continue;
    }
    if (char === "'") {
      inSingle = true;
      continue;
    }
    if (char === '"') {
      inDouble = true;
      continue;
    }
    if (char === "(") {
      depth += 1;
      continue;
    }
    if (char === ")") {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (char === "=" && depth === 0) {
      return index;
    }
  }
  return -1;
}

function unquoteLoose(value: string): string {
  return value.replace(/^["']|["']$/g, "");
}

function humanizeName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, char => char.toUpperCase());
}
